import React, { useRef } from 'react'
import { LeafletMap, LeafletMapRef } from './LeafletMap'
import {
  MapAutonomousCommunity,
  MapMunicipality,
  MapMunicipalityMetric,
  MapProvince,
  MapProvinceMetric,
  MapSectionMetric,
  MapYear,
} from '../services/map-municipalities.service'
import { MapMetric } from '../services/map-metrics.service'
import { Helmet } from 'react-helmet'
import { Button, Card, Row, Col } from 'antd'
import styled from 'styled-components'
import { MapMeta } from './MapMeta'
import { DownloadOutlined, FileExcelOutlined, FileImageOutlined } from '@ant-design/icons'
import { getAbsoluteServerUrl } from '../config/config'
import { MapSelectYear } from '../layouts/components/MapSelectYear'

type Props = {
  autonomousCommunities: MapAutonomousCommunity[]
  provinces: MapProvince[]
  municipalities: MapMunicipality[]
  metric: MapMetric
  year?: MapYear
  geographicalData: any
  metricData: MapSectionMetric[] | MapMunicipalityMetric[] | MapProvinceMetric[]
  seriesOptions: any
  years: MapYear[]
  handleYearSelection: (year: MapYear) => void
}

const joinListOfNames = (names: string[]): string => {
  if (!names.length) {
    return ''
  }
  if (names.length === 1) {
    return names[0]
  }
  if (names.length === 2) {
    return `${names[0]} y ${names[1]}`
  }
  return `${names[0]}, ${joinListOfNames(names.slice(1))}`
}

export const Map: React.FC<Props> = ({
  autonomousCommunities,
  provinces,
  municipalities,
  metric,
  year,
  geographicalData,
  metricData,
  seriesOptions,
  years,
  handleYearSelection,
}) => {
  const leafletRef = useRef<LeafletMapRef>(null)

  const title =
    joinListOfNames([...autonomousCommunities, ...provinces, ...municipalities].map(it => it.name)) || 'España'
  const range = metric.range ? { min: metric.range[0], max: metric.range[1] } : {}

  return (
    <div>
      <Helmet>
        <title>{`${metric.name} en ${title} durante ${year}`}</title>
        <meta name="description" content={metric.description?.substring(0, 170)} />
        <meta name="keywords" content="Serie temporal, periodicidad, unidades, fuentes, descripción" />
        <meta name="author" content="OTEA: Observatorio de la transición energética y la acción climática" />
      </Helmet>
      <NoPaddingCard
        title={
          <Row align="middle" justify="center">
            <Col style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MapSelectYear handleYearSelection={handleYearSelection} years={years} selectedYear={year} />
              <Button href={getAbsoluteServerUrl(`/map/download`)} target="_blank" rel="noopener noreferrer">
                {' '}
                <FileExcelOutlined />
                <DownloadOutlined />
              </Button>
              <Button
                onClick={() => {
                  if (leafletRef.current) {
                    leafletRef.current.exportImage()
                  }
                }}
              >
                <FileImageOutlined />
                <DownloadOutlined />
              </Button>
            </Col>
          </Row>
        }
      >
        <LeafletMap
          ref={leafletRef}
          autonomousCommunities={autonomousCommunities}
          provinces={provinces}
          municipalities={municipalities}
          metric={metric}
          year={year}
          geographicalData={geographicalData}
          metricData={metricData}
          seriesOptions={seriesOptions}
          years={years}
          handleYearSelection={handleYearSelection}
        />
        <MapMeta year={year} metric={metric} />
      </NoPaddingCard>
    </div>
  )
}

const NoPaddingCard = styled(Card)`
  .ant-card-body {
    padding: 0;
  }

  .ant-tabs-tabpane > div {
    padding: 0;
  }
`
